import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, MapPin, Clock, Sparkles, Heart, Leaf, Calendar } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import LaunchBanner from "@/components/LaunchBanner";
import Footer from "@/components/Footer";
import { IMAGES } from "@/lib/images";
import api from "@/lib/api";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: "easeOut" } }),
};

export default function Home() {
  const [sessions, setSessions] = useState([]);
  useEffect(() => { api.get("/public/schedule").then((r) => setSessions(r.data.slice(0, 3))).catch(() => {}); }, []);

  return (
    <div className="bg-white text-brand-ink">
      <LaunchBanner />
      <PublicNav topClass="top-11" />

      {/* Hero */}
      <section className="pt-36 lg:pt-44 pb-16 px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={fade} className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.35em] text-brand-red font-semibold mb-6">Salt Lake City · Utah</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-[4.2rem] leading-[0.98] tracking-tight">
              Yoga for <span className="text-brand-red">paws</span> & their people.
            </h1>
            <p className="mt-8 font-serif text-2xl leading-relaxed text-brand-ink/70 max-w-lg">
              Guided myofascial release yoga for pets and their parents — practiced together, outdoors, in the parks you love.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/book" className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors" data-testid="hero-book-btn">
                Book a Session <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/schedule" className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-brand-line text-brand-ink font-medium hover:border-brand-red hover:text-brand-red transition-colors" data-testid="hero-schedule-btn">
                See Park Schedule
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-8 text-sm text-brand-ink/60">
              <div><span className="font-display text-2xl text-brand-ink">45</span> min sessions</div>
              <div className="h-8 w-px bg-brand-line" />
              <div><span className="font-display text-2xl text-brand-red">$60</span> in-app · <span className="font-display text-2xl text-brand-ink">$65</span> online</div>
            </div>
          </motion.div>
          <motion.div initial="hidden" animate="show" variants={fade} custom={1} className="lg:col-span-7">
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl">
              <img src={IMAGES.hero} alt="Dog stretched on a yoga mat in a Salt Lake City park" className="w-full h-[380px] lg:h-[560px] object-cover" data-testid="hero-image" />
              <div className="absolute bottom-5 left-5 bg-white/85 backdrop-blur-md rounded-2xl px-5 py-3 flex items-center gap-3">
                <Leaf className="w-5 h-5 text-brand-red" />
                <span className="text-sm font-medium">Myofascial release · body & breath</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee ribbon */}
      <div className="border-y border-brand-line bg-brand-mist py-4">
        <Marquee speed={40} gradient={false}>
          {["Myofascial Release", "Pets & Parents", "Outdoor Parks", "Salt Lake City", "45-Minute Flows", "Calm · Connection · Mobility"].map((t, i) => (
            <span key={i} className="mx-8 font-display text-lg tracking-widest text-brand-ink/70 flex items-center gap-8">
              {t} <span className="text-brand-red">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* Approach */}
      <section id="approach" className="py-24 px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="rounded-[2rem] overflow-hidden">
            <img src={IMAGES.ownerDog} alt="Owner and dog practicing yoga together" className="w-full h-[480px] object-cover" data-testid="approach-image" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.35em] text-brand-red font-semibold mb-5 sanskrit-underline inline-block">The Approach</div>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">A practice you share with your dog.</h2>
            <p className="mt-6 font-serif text-xl leading-relaxed text-brand-ink/70">
              Myofascial release eases tension held in the body's connective tissue. Together we guide both you and your pet through gentle stretches, breath, and touch — building mobility, calm, and a deeper bond.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              {[
                { icon: Heart, title: "For pets & parents", body: "Every session moves you both — a shared ritual, not a spectator sport." },
                { icon: Leaf, title: "Fascia-first", body: "Slow, intentional myofascial work to release tension and restore ease." },
                { icon: MapPin, title: "In the parks", body: "Held outdoors across Salt Lake City — follow along to find us." },
                { icon: Sparkles, title: "Truly unique", body: "The only myofascial pet-and-parent yoga practice in Utah." },
              ].map((f, i) => (
                <div key={i} className="p-6 rounded-2xl border border-brand-line hover:border-brand-red hover:-translate-y-1 transition-all duration-300" data-testid={`approach-card-${i}`}>
                  <f.icon className="w-6 h-6 text-brand-red" />
                  <div className="mt-4 font-semibold">{f.title}</div>
                  <p className="mt-2 text-sm text-brand-ink/60 leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming schedule teaser */}
      <section className="py-20 px-6 lg:px-10 bg-brand-mist border-y border-brand-line">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.35em] text-brand-red font-semibold mb-4">Follow Us</div>
              <h2 className="font-display text-4xl sm:text-5xl">Where we'll be next</h2>
            </div>
            <Link to="/schedule" className="inline-flex items-center gap-2 text-brand-red font-medium hover:gap-3 transition-all" data-testid="teaser-view-all">
              View full schedule <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {sessions.length === 0 ? (
            <p className="text-brand-ink/50 font-serif text-xl" data-testid="teaser-empty">Fresh sessions are being scheduled — check back soon or book a private time.</p>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {sessions.map((s) => (
                <div key={s.id} className="bg-white rounded-2xl p-6 border border-brand-line" data-testid={`teaser-session-${s.id}`}>
                  <div className="flex items-center gap-2 text-brand-red text-sm font-medium"><MapPin className="w-4 h-4" /> {s.park_name}</div>
                  <div className="mt-3 font-display text-2xl">{s.date}</div>
                  <div className="mt-2 flex items-center gap-2 text-sm text-brand-ink/60"><Clock className="w-4 h-4" /> {s.time} · {s.duration_min} min</div>
                  <Link to={`/book?session=${s.id}`} className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-brand-ink hover:text-brand-red transition-colors">Prebook <ArrowRight className="w-4 h-4" /></Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 px-6 lg:px-10 max-w-7xl mx-auto">
        <h2 className="font-display text-4xl sm:text-5xl text-center mb-14">Moments on the mat</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[IMAGES.gallery1, IMAGES.ownerDog, IMAGES.gallery2, IMAGES.gallery3, IMAGES.hero].map((src, i) => (
            <div key={i} className={`overflow-hidden rounded-2xl ${i === 1 ? 'md:row-span-2' : ''}`}>
              <img src={src} alt="Paws N Poses gallery" className="w-full h-full min-h-[200px] object-cover hover:scale-105 transition-transform duration-500" data-testid={`gallery-img-${i}`} />
            </div>
          ))}
        </div>
      </section>

      {/* Pricing / CTA */}
      <section className="py-24 px-6 lg:px-10">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
          {[
            { name: "In-App Prebook", price: "$60", note: "Best value · reserve from the app", featured: true },
            { name: "Online Booking", price: "$65", note: "Book a session on the web", featured: false },
          ].map((p) => (
            <div key={p.name} className={`rounded-[2rem] p-10 border ${p.featured ? 'bg-brand-ink text-white border-brand-ink' : 'bg-white border-brand-line'}`} data-testid={`pricing-${p.name.toLowerCase().replace(/\s/g,'-')}`}>
              <div className={`text-xs uppercase tracking-[0.3em] ${p.featured ? 'text-brand-red' : 'text-brand-red'}`}>{p.name}</div>
              <div className="mt-6 font-display text-6xl">{p.price}</div>
              <div className={`mt-3 text-sm ${p.featured ? 'text-white/60' : 'text-brand-ink/60'}`}>{p.note}</div>
              <div className={`mt-2 text-sm ${p.featured ? 'text-white/60' : 'text-brand-ink/60'}`}>45-minute guided session</div>
              <Link to="/book" className={`mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium transition-colors ${p.featured ? 'bg-brand-red text-white hover:bg-brand-reddark' : 'border border-brand-line hover:border-brand-red hover:text-brand-red'}`} data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s/g,'-')}`}>
                <Calendar className="w-4 h-4" /> Book now
              </Link>
            </div>
          ))}
        </div>
        <div className="max-w-5xl mx-auto mt-8 text-center">
          <Link to="/bundles" className="inline-flex items-center gap-2 text-brand-red font-medium hover:gap-3 transition-all" data-testid="pricing-bundles-link">Save more with 5 &amp; 10-session packs <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
